import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Briefcase, CalendarClock, CheckCircle2 } from 'lucide-react';
import type { User } from '../types';
import type {
  ActivityFeedItem,
  InterviewRow,
  OfferRow,
  ProposalRow,
  RecruiterNotification,
  MockVacancy,
} from '../data/clientEngagementMock';
import {
  supabase_get_portal_engagement_applications,
  supabase_get_portal_engagement_interviews,
  supabase_get_portal_engagement_vacancy_ids,
  supabase_get_placements,
  supabase_get_vacancy_by_id,
  supabase_portal_update_application_status,
  supabase_portal_append_placement_client_note,
  supabase_portal_request_interview_reschedule,
  supabase_append_portal_thread_message,
  extractErrorMessage,
} from '../services/supabaseService';
import {
  buildActivityFromApplications,
  mapApplicationToProposalRow,
  mapInterviewRow,
  mapPlacementToOfferRow,
  proposalNeedsClientAttention,
  quickActionToDbStatus,
  isPipelineVoorgesteld,
  interviewFollowupToApplicationStatus,
} from '../utils/portalEngagementMappers';
import DashboardHeader from './clientEngagementCenter/DashboardHeader';
import CandidateProposalCard from './clientEngagementCenter/CandidateProposalCard';
import InterviewAgenda from './clientEngagementCenter/InterviewAgenda';
import type { PortalInterviewFollowUpKind } from './clientEngagementCenter/UpcomingInterviewCard';
import OfferCard from './clientEngagementCenter/OfferCard';
import EmptyState from './clientEngagementCenter/EmptyState';
import LoadingState from './clientEngagementCenter/LoadingState';
import RecruiterHeadsUpToast, { dispatchGlobalToast } from './clientEngagementCenter/RecruiterHeadsUpToast';
import type { QuickClientAction } from './clientEngagementCenter/QuickActionButtons';
import EngagementVacancyModal from './clientEngagementCenter/EngagementVacancyModal';
import EngagementCandidateModal from './clientEngagementCenter/EngagementCandidateModal';
import { downloadPortalClientCv } from '../utils/portalClientCvDownload';

function uid(prefix: string) {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function scrollToSection(id: string) {
  if (typeof document === 'undefined') return;
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

type UndoApplicationMeta = { kind: 'application'; applicationId: string; previousStatus: string };

export interface ClientEngagementCenterProps {
  user: User;
}

const ClientEngagementCenter: React.FC<ClientEngagementCenterProps> = ({ user }) => {
  const [proposals, setProposals] = useState<ProposalRow[]>([]);
  const [interviews, setInterviews] = useState<InterviewRow[]>([]);
  const [offers, setOffers] = useState<OfferRow[]>([]);
  const [activity, setActivity] = useState<ActivityFeedItem[]>([]);
  const [, setNotifications] = useState<RecruiterNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [vacancyFilter, setVacancyFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [localToast, setLocalToast] = useState<{ message: string; type?: 'success' | 'info' | 'error' } | null>(null);

  const [vacancyModal, setVacancyModal] = useState<{
    open: boolean;
    vacancyId: string | null;
    loading: boolean;
    error: string | null;
    data: Record<string, any> | null;
  }>({ open: false, vacancyId: null, loading: false, error: null, data: null });

  const [candidateModal, setCandidateModal] = useState<{ open: boolean; proposal: ProposalRow | null }>({
    open: false,
    proposal: null,
  });

  const [notes, setNotes] = useState<Record<string, string>>({});

  const undoRef = useRef<{ meta: UndoApplicationMeta; timer: ReturnType<typeof setTimeout> } | null>(null);
  const [undoUntil, setUndoUntil] = useState<number | null>(null);

  const clientId = String(user.clientId || '').trim();

  const vacancyOptions = useMemo((): MockVacancy[] => {
    const m = new Map<string, string>();
    proposals.forEach((p) => {
      if (p.vacancyId) m.set(p.vacancyId, p.vacancyTitle);
    });
    return Array.from(m.entries()).map(([id, title]) => ({
      id,
      title,
      clientId: clientId || '',
    }));
  }, [proposals, clientId]);

  const proposalsRef = useRef<ProposalRow[]>([]);

  const loadEngagement = useCallback(async () => {
    if (!clientId) {
      proposalsRef.current = [];
      setProposals([]);
      setInterviews([]);
      setOffers([]);
      setActivity([]);
      setLoading(false);
      setLoadError('Geen organisatie gekoppeld aan je account.');
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const vacIds = await supabase_get_portal_engagement_vacancy_ids(clientId);
      const [appsRaw, intsRaw, placementsRaw] = await Promise.all([
        supabase_get_portal_engagement_applications(vacIds),
        supabase_get_portal_engagement_interviews(vacIds),
        supabase_get_placements(clientId, undefined, true),
      ]);
      const proposalsMapped = (appsRaw || []).map((a) => mapApplicationToProposalRow(a, clientId));
      proposalsRef.current = proposalsMapped;
      const interviewsMapped = (intsRaw || []).map((r) => mapInterviewRow(r, clientId));
      const offersMapped = (placementsRaw || []).map((p) => mapPlacementToOfferRow(p, clientId));
      setProposals(proposalsMapped);
      setInterviews(interviewsMapped);
      setOffers(offersMapped);
      setActivity(buildActivityFromApplications(proposalsMapped, 12));
    } catch (e) {
      console.error('[ClientEngagementCenter] load', e);
      setLoadError(extractErrorMessage(e) || 'Kon gegevens niet laden.');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    void loadEngagement();
  }, [loadEngagement]);

  const scheduleUndo = useCallback((meta: UndoApplicationMeta) => {
    if (undoRef.current?.timer) clearTimeout(undoRef.current.timer);
    const until = Date.now() + 10_000;
    setUndoUntil(until);
    const timer = setTimeout(() => {
      undoRef.current = null;
      setUndoUntil(null);
    }, 10_000);
    undoRef.current = { meta, timer };
  }, []);

  const applyUndo = useCallback(async () => {
    const u = undoRef.current;
    if (!u) return;
    clearTimeout(u.timer);
    undoRef.current = null;
    setUndoUntil(null);
    if (u.meta.kind === 'application') {
      const { error } = await supabase_portal_update_application_status(u.meta.applicationId, u.meta.previousStatus);
      if (error) {
        setLocalToast({ message: extractErrorMessage(error) || 'Terugdraaien mislukt', type: 'error' });
      } else {
        dispatchGlobalToast('Laatste statuswijziging ongedaan gemaakt', 'info');
      }
    }
    await loadEngagement();
  }, [loadEngagement]);

  const noteKey = (type: 'proposal' | 'interview' | 'offer', id: string) => `${type}:${id}`;

  const openVacancyModal = useCallback((vacancyId: string) => {
    setVacancyModal({ open: true, vacancyId, loading: true, error: null, data: null });
    void (async () => {
      const data = await supabase_get_vacancy_by_id(vacancyId);
      if (!data) {
        setVacancyModal((s) => ({ ...s, loading: false, error: 'Vacature kon niet worden geladen of je hebt geen toegang.' }));
        return;
      }
      setVacancyModal((s) => ({ ...s, loading: false, data: data as Record<string, any>, error: null }));
    })();
  }, []);

  const closeVacancyModal = useCallback(() => {
    setVacancyModal({ open: false, vacancyId: null, loading: false, error: null, data: null });
  }, []);

  const openCandidateModal = useCallback((p: ProposalRow) => {
    setCandidateModal({ open: true, proposal: p });
  }, []);

  const closeCandidateModal = useCallback(() => {
    setCandidateModal({ open: false, proposal: null });
  }, []);

  const handleDownloadLatestCv = useCallback(async (p: ProposalRow) => {
    try {
      await downloadPortalClientCv(p);
      dispatchGlobalToast('CV wordt gedownload', 'success');
    } catch (e) {
      console.error('[ClientEngagementCenter] CV download', e);
      dispatchGlobalToast(e instanceof Error ? e.message : 'CV downloaden mislukt', 'error');
    }
  }, []);

  const pushNotification = useCallback(
    (partial: Omit<RecruiterNotification, 'id' | 'createdAt' | 'read'>) => {
      const row: RecruiterNotification = {
        ...partial,
        id: uid('n'),
        createdAt: new Date().toISOString(),
        read: false,
      };
      setNotifications((prev) => [row, ...prev]);
      if (import.meta.env.DEV) console.debug('[ClientEngagementCenter] recruiter notification', row);
    },
    [],
  );

  const pushActivity = useCallback((item: Omit<ActivityFeedItem, 'id' | 'createdAt'>) => {
    const row: ActivityFeedItem = {
      ...item,
      id: uid('a'),
      createdAt: new Date().toISOString(),
    };
    setActivity((prev) => [row, ...prev].slice(0, 20));
  }, []);

  const filteredProposals = useMemo(() => {
    const q = search.trim().toLowerCase();
    return proposals.filter((p) => {
      if (vacancyFilter !== 'all' && p.vacancyId !== vacancyFilter) return false;
      if (statusFilter !== 'all' && p.status !== statusFilter) return false;
      if (!q) return true;
      return (
        p.name.toLowerCase().includes(q) ||
        p.vacancyTitle.toLowerCase().includes(q) ||
        p.skills.some((s) => s.toLowerCase().includes(q))
      );
    });
  }, [proposals, search, vacancyFilter, statusFilter]);

  /** Zelfde dossiers als Kanban «Voorgesteld»: portaal-publicatie / snapshot, niet alleen ruwe pipeline-tekst. */
  const voorgesteldBucketAll = useMemo(
    () => filteredProposals.filter((p) => p.showInClientVoorgesteldEngagement === true),
    [filteredProposals],
  );

  /** Actief te beoordelen — niet meer na klant-afwijzing (die staan onder «Extra: afgewezen»). */
  const voorgesteldList = useMemo(
    () => voorgesteldBucketAll.filter((p) => p.status !== 'rejected'),
    [voorgesteldBucketAll],
  );

  const voorgesteldAfgewezenList = useMemo(
    () => voorgesteldBucketAll.filter((p) => p.status === 'rejected'),
    [voorgesteldBucketAll],
  );

  const overigeActieList = useMemo(
    () =>
      filteredProposals.filter(
        (p) =>
          p.showInClientVoorgesteldEngagement !== true &&
          proposalNeedsClientAttention(p) &&
          !isPipelineVoorgesteld(p.pipelineStatus) &&
          p.status !== 'waiting_feedback',
      ),
    [filteredProposals],
  );

  const kpiTeBeoordelen = voorgesteldList.length + overigeActieList.length;
  const kpiInterviews = interviews.length;
  const kpiPlaatsingen = offers.length;

  const resolveApplicationId = useCallback(
    (candidateId: string, vacancyId: string) =>
      proposals.find((p) => p.id === candidateId && p.vacancyId === vacancyId)?.proposalId ?? null,
    [proposals],
  );

  const handleQuickProposal = useCallback(
    async (proposalId: string, action: QuickClientAction) => {
      const proposal = proposals.find((p) => p.proposalId === proposalId);
      if (!proposal) return;
      const prev = proposal.pipelineStatus ?? '';
      const dbNew = quickActionToDbStatus(action);
      const comment = (notes[noteKey('proposal', proposalId)] || '').trim();
      const { error } = await supabase_portal_update_application_status(proposalId, dbNew, comment ? { comment } : undefined);
      if (error) {
        setLocalToast({ message: extractErrorMessage(error) || 'Opslaan mislukt', type: 'error' });
        return;
      }
      scheduleUndo({ kind: 'application', applicationId: proposalId, previousStatus: prev });
      const labels: Record<QuickClientAction, string> = {
        niet_passend: 'Niet passend',
        later: 'Later beoordelen',
        meer_info: 'Meer info nodig',
        interview: 'Interview gewenst',
      };
      pushNotification({
        type: 'client_action',
        candidateId: proposal.id,
        vacancyId: proposal.vacancyId,
        clientId,
        recruiterId: proposal.recruiterId,
        action,
        note: comment,
        priority: action === 'interview' ? 'high' : 'normal',
      });
      dispatchGlobalToast(`${labels[action]} opgeslagen`, 'success');
      await loadEngagement();
      setActivity((prev) =>
        [
          {
            id: uid('a'),
            createdAt: new Date().toISOString(),
            icon: 'user',
            actor: 'client',
            actorLabel: user.name || 'Klant',
            description: `${labels[action]} — ${proposal.name}`,
            candidateName: proposal.name,
            vacancyTitle: proposal.vacancyTitle,
          } as ActivityFeedItem,
          ...buildActivityFromApplications(proposalsRef.current, 10),
        ].slice(0, 20),
      );
    },
    [proposals, scheduleUndo, pushNotification, clientId, user.name, notes, loadEngagement],
  );

  const handleInviteDecision = useCallback(
    async (proposalId: string, opts: { accept: boolean; comment: string }) => {
      const proposal = proposals.find((p) => p.proposalId === proposalId);
      if (!proposal) return;
      const prev = proposal.pipelineStatus ?? '';
      const action: QuickClientAction = opts.accept ? 'interview' : 'niet_passend';
      const dbNew = quickActionToDbStatus(action);
      const mergedComment = [opts.comment.trim(), notes[noteKey('proposal', proposalId)]?.trim()]
        .filter(Boolean)
        .join('\n\n');
      const { error } = await supabase_portal_update_application_status(
        proposalId,
        dbNew,
        mergedComment ? { comment: mergedComment } : undefined,
      );
      if (error) {
        setLocalToast({ message: extractErrorMessage(error) || 'Opslaan mislukt', type: 'error' });
        return;
      }
      scheduleUndo({ kind: 'application', applicationId: proposalId, previousStatus: prev });
      pushNotification({
        type: 'client_invite_decision',
        candidateId: proposal.id,
        vacancyId: proposal.vacancyId,
        clientId,
        recruiterId: proposal.recruiterId,
        action: (opts.accept ? 'door_naar_interview' : 'niet_passend') as any,
        note: mergedComment,
        priority: opts.accept ? 'high' : 'normal',
      });
      pushActivity({
        icon: 'user',
        actor: 'client',
        actorLabel: user.name || 'Klant',
        description: opts.accept ? `Uitnodigen: ja — ${proposal.name}` : `Uitnodigen: nee — ${proposal.name}`,
        candidateName: proposal.name,
        vacancyTitle: proposal.vacancyTitle,
      });
      dispatchGlobalToast(opts.accept ? 'Uitnodiging akkoord doorgegeven' : 'Afwijzing doorgegeven', 'success');
      await loadEngagement();
    },
    [proposals, scheduleUndo, pushNotification, clientId, user.name, notes, loadEngagement, pushActivity],
  );

  const handleInterviewPortalFollowUp = useCallback(
    async (inv: InterviewRow, kind: PortalInterviewFollowUpKind, comment: string) => {
      if (kind === 'reschedule') {
        const { error } = await supabase_portal_request_interview_reschedule(inv.id);
        if (error) {
          setLocalToast({
            message: extractErrorMessage(error) || 'Verplaatsing kon niet worden geregistreerd (database).',
            type: 'error',
          });
          return;
        }
        setInterviews((prev) =>
          prev.map((x) => (x.id === inv.id ? { ...x, status: 'reschedule_requested' as const } : x)),
        );
        const appId = resolveApplicationId(inv.candidateId, inv.vacancyId);
        if (appId && comment.trim()) {
          await supabase_portal_update_application_status(appId, interviewFollowupToApplicationStatus('next_round'), {
            comment: `Verplaatsing aangevraagd (gesprek): ${comment.trim()}`,
          });
        }
        pushActivity({
          icon: 'calendar',
          actor: 'client',
          actorLabel: user.name || 'Klant',
          description: 'Verplaatsing gesprek aangevraagd',
          candidateName: inv.candidateName,
          vacancyTitle: inv.vacancyTitle,
        });
        dispatchGlobalToast('Verzoek tot verplaatsing verstuurd', 'success');
        return;
      }

      const appId = resolveApplicationId(inv.candidateId, inv.vacancyId);
      if (!appId) {
        setLocalToast({
          message: 'Geen gekoppelde sollicitatie gevonden voor deze kandidaat. Neem contact op met recruitment.',
          type: 'error',
        });
        return;
      }
      const status =
        kind === 'next_round'
          ? interviewFollowupToApplicationStatus('next_round')
          : interviewFollowupToApplicationStatus('reject');
      const { error } = await supabase_portal_update_application_status(appId, status, comment.trim() ? { comment } : undefined);
      if (error) {
        setLocalToast({ message: extractErrorMessage(error) || 'Opslaan mislukt', type: 'error' });
        return;
      }
      pushNotification({
        type: 'interview_client_followup',
        candidateId: inv.candidateId,
        vacancyId: inv.vacancyId,
        clientId,
        recruiterId: inv.recruiterId,
        action: kind as any,
        note: comment,
        priority: 'high',
      });
      pushActivity({
        icon: 'message',
        actor: 'client',
        actorLabel: user.name || 'Klant',
        description: kind === 'next_round' ? 'Akkoord volgende ronde na gesprek' : 'Afwijzing na gesprek',
        candidateName: inv.candidateName,
        vacancyTitle: inv.vacancyTitle,
      });
      dispatchGlobalToast('Feedback opgeslagen', 'success');
      await loadEngagement();
    },
    [clientId, user.name, loadEngagement, pushActivity, pushNotification, resolveApplicationId],
  );

  const saveNote = useCallback((type: 'proposal' | 'interview' | 'offer', entityId: string, text: string) => {
    const k = noteKey(type, entityId);
    setNotes((prev) => ({ ...prev, [k]: text }));
  }, []);

  const handlePortalThreadReply = useCallback(
    async (proposalId: string, text: string) => {
      const { error } = await supabase_append_portal_thread_message(proposalId, text, 'client');
      if (error) {
        setLocalToast({ message: extractErrorMessage(error) || 'Bericht verzenden mislukt', type: 'error' });
        return;
      }
      dispatchGlobalToast('Bericht verstuurd aan recruitment', 'success');
      await loadEngagement();
    },
    [loadEngagement],
  );

  const sendHeadsUp = useCallback(
    (
      _type: 'proposal' | 'interview' | 'offer',
      entityId: string,
      text: string,
      meta: { candidateId: string; vacancyId: string; recruiterId: string; candidateName?: string; vacancyTitle?: string },
    ) => {
      const trimmed = text.trim();
      if (!trimmed) {
        setLocalToast({ message: 'Voeg eerst een korte notitie toe.', type: 'error' });
        return;
      }
      pushNotification({
        type: 'client_note',
        candidateId: meta.candidateId,
        vacancyId: meta.vacancyId,
        clientId,
        recruiterId: meta.recruiterId,
        action: 'feedback_note',
        note: trimmed,
        priority: 'normal',
      });
      pushActivity({
        icon: 'message',
        actor: 'client',
        actorLabel: user.name || 'Klant',
        description: 'Bericht naar recruitment',
        candidateName: meta.candidateName,
        vacancyTitle: meta.vacancyTitle,
      });
      dispatchGlobalToast('Bericht geregistreerd', 'success');
    },
    [clientId, pushActivity, pushNotification, user.name],
  );

  const handleSavePlacementComment = useCallback(
    async (placementId: string, text: string) => {
      const trimmed = text.trim();
      if (!trimmed) {
        setLocalToast({ message: 'Typ eerst een korte toelichting.', type: 'error' });
        return;
      }
      const { error } = await supabase_portal_append_placement_client_note(placementId, trimmed);
      if (error) {
        setLocalToast({ message: extractErrorMessage(error) || 'Opslaan mislukt', type: 'error' });
        return;
      }
      dispatchGlobalToast('Commentaar opgeslagen bij de plaatsing', 'success');
      pushActivity({
        icon: 'sparkles',
        actor: 'client',
        actorLabel: user.name || 'Klant',
        description: 'Commentaar bij plaatsing',
        candidateName: offers.find((o) => o.id === placementId)?.candidateName,
        vacancyTitle: offers.find((o) => o.id === placementId)?.vacancyTitle,
      });
    },
    [offers, pushActivity, user.name],
  );

  const noteTextByInterviewId = useMemo(() => {
    const o: Record<string, string> = {};
    interviews.forEach((i) => {
      o[i.id] = notes[noteKey('interview', i.id)] || '';
    });
    return o;
  }, [interviews, notes]);

  return (
    <div className="relative flex min-h-full flex-col bg-[#f4f6f8]">
      <DashboardHeader
        search={search}
        onSearchChange={setSearch}
        vacancyFilter={vacancyFilter}
        onVacancyFilterChange={setVacancyFilter}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        vacancies={vacancyOptions}
        portalSimple
      />

      <div className="pointer-events-none fixed right-4 top-20 z-[95] flex flex-col items-end gap-2 sm:top-24">
        <div className="pointer-events-auto">
          <RecruiterHeadsUpToast payload={localToast} onDismiss={() => setLocalToast(null)} />
        </div>
      </div>

      {undoUntil && Date.now() < undoUntil && (
        <div className="fixed bottom-24 left-1/2 z-[96] flex -translate-x-1/2 items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 shadow-xl lg:bottom-8">
          <span className="text-xs font-medium text-slate-700">Laatste wijziging ongedaan maken?</span>
          <button
            type="button"
            onClick={() => void applyUndo()}
            className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800"
          >
            Ongedaan
          </button>
        </div>
      )}

      {loadError ? (
        <div className="mx-auto max-w-lg px-4 py-8">
          <EmptyState title="Gegevens niet beschikbaar" description={loadError} />
        </div>
      ) : null}

      {!loadError ? (
        <div className="mx-auto w-full max-w-5xl flex-1 space-y-10 px-4 py-6 sm:px-6 lg:px-8 lg:py-10">
          {loading && proposals.length === 0 ? (
            <LoadingState message="Gegevens laden…" />
          ) : null}

          <section className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <button
              type="button"
              onClick={() => {
                if (voorgesteldList.length) scrollToSection('cec-voorgesteld');
                else if (overigeActieList.length) scrollToSection('cec-overige');
                else scrollToSection('cec-voorgesteld');
              }}
              className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:border-slate-300"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50 text-amber-700">
                <AlertCircle className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-semibold tabular-nums text-slate-900">{kpiTeBeoordelen}</p>
                <p className="text-xs font-medium text-slate-500">Te beoordelen</p>
              </div>
            </button>
            <button
              type="button"
              onClick={() => scrollToSection('cec-agenda')}
              className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:border-slate-300"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
                <CalendarClock className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-semibold tabular-nums text-slate-900">{kpiInterviews}</p>
                <p className="text-xs font-medium text-slate-500">Gesprekken (venster)</p>
              </div>
            </button>
            <button
              type="button"
              onClick={() => scrollToSection('cec-plaatsingen')}
              className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:border-slate-300"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
                <CheckCircle2 className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-semibold tabular-nums text-slate-900">{kpiPlaatsingen}</p>
                <p className="text-xs font-medium text-slate-500">Plaatsingen</p>
              </div>
            </button>
          </section>

          <section id="cec-voorgesteld" className="scroll-mt-6">
            <div className="mb-4 flex items-end justify-between gap-2 border-b border-slate-200 pb-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Voorgestelde kandidaten</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Geef aan of je de kandidaat wilt uitnodigen voor een gesprek. Je toelichting wordt bij het dossier
                  bewaard.
                </p>
              </div>
              <span className="shrink-0 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                {voorgesteldList.length}
              </span>
            </div>
            <div className="space-y-5">
              {voorgesteldList.length ? (
                voorgesteldList.map((p) => (
                  <CandidateProposalCard
                    key={p.proposalId}
                    proposal={p}
                    noteText={notes[noteKey('proposal', p.proposalId)] || ''}
                    onAutosaveNote={(id, t) => saveNote('proposal', id, t)}
                    onSendHeadsUp={(id, t) =>
                      sendHeadsUp('proposal', id, t, {
                        candidateId: p.id,
                        vacancyId: p.vacancyId,
                        recruiterId: p.recruiterId,
                        candidateName: p.name,
                        vacancyTitle: p.vacancyTitle,
                      })
                    }
                    decisionMode
                    onInviteDecision={handleInviteDecision}
                    onQuickAction={handleQuickProposal}
                    onVacancyClick={openVacancyModal}
                    onCandidateProfileClick={() => openCandidateModal(p)}
                    onDownloadLatestCv={() => handleDownloadLatestCv(p)}
                    onPortalThreadReply={handlePortalThreadReply}
                  />
                ))
              ) : (
                <EmptyState
                  title="Geen voorgestelde kandidaten"
                  description="Als je recruiter iemand voorstelt, verschijnt die hier meteen."
                />
              )}
            </div>
          </section>

          {voorgesteldAfgewezenList.length > 0 ? (
            <section id="cec-afgewezen-extra" className="scroll-mt-6">
              <div className="mb-4 flex items-end justify-between gap-2 border-b border-slate-200 pb-3">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Extra: afgewezen kandidaten</h2>
                  <p className="mt-1 text-sm text-slate-500">
                    Dossiers die je via het portaal hebt afgewezen (of die als afgewezen in de pipeline staan). Niet
                    meer nodig voor je actielijst; alleen ter inzicht.
                  </p>
                </div>
                <span className="shrink-0 rounded-full bg-slate-200 px-3 py-1 text-xs font-medium text-slate-700">
                  {voorgesteldAfgewezenList.length}
                </span>
              </div>
              <div className="space-y-5">
                {voorgesteldAfgewezenList.map((p) => (
                  <CandidateProposalCard
                    key={p.proposalId}
                    proposal={p}
                    noteText={notes[noteKey('proposal', p.proposalId)] || ''}
                    onAutosaveNote={(id, t) => saveNote('proposal', id, t)}
                    onSendHeadsUp={(id, t) =>
                      sendHeadsUp('proposal', id, t, {
                        candidateId: p.id,
                        vacancyId: p.vacancyId,
                        recruiterId: p.recruiterId,
                        candidateName: p.name,
                        vacancyTitle: p.vacancyTitle,
                      })
                    }
                    onQuickAction={handleQuickProposal}
                    onVacancyClick={openVacancyModal}
                    onCandidateProfileClick={() => openCandidateModal(p)}
                    onDownloadLatestCv={() => handleDownloadLatestCv(p)}
                    onPortalThreadReply={handlePortalThreadReply}
                  />
                ))}
              </div>
            </section>
          ) : null}

          {overigeActieList.length > 0 ? (
            <section id="cec-overige" className="scroll-mt-6">
              <div className="mb-4 flex items-end justify-between gap-2 border-b border-slate-200 pb-3">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Overige openstaande dossiers</h2>
                  <p className="mt-1 text-sm text-slate-500">Meer info, on hold of andere actie vanuit recruitment.</p>
                </div>
              </div>
              <div className="space-y-5">
                {overigeActieList.map((p) => (
                  <CandidateProposalCard
                    key={p.proposalId}
                    proposal={p}
                    noteText={notes[noteKey('proposal', p.proposalId)] || ''}
                    onAutosaveNote={(id, t) => saveNote('proposal', id, t)}
                    onSendHeadsUp={(id, t) =>
                      sendHeadsUp('proposal', id, t, {
                        candidateId: p.id,
                        vacancyId: p.vacancyId,
                        recruiterId: p.recruiterId,
                        candidateName: p.name,
                        vacancyTitle: p.vacancyTitle,
                      })
                    }
                    onQuickAction={handleQuickProposal}
                    onVacancyClick={openVacancyModal}
                    onCandidateProfileClick={() => openCandidateModal(p)}
                    onDownloadLatestCv={() => handleDownloadLatestCv(p)}
                    onPortalThreadReply={handlePortalThreadReply}
                  />
                ))}
              </div>
            </section>
          ) : null}

          <section id="cec-agenda" className="scroll-mt-6">
            <div className="mb-4 border-b border-slate-200 pb-3">
              <h2 className="text-lg font-semibold text-slate-900">Agenda gesprekken</h2>
              <p className="mt-1 text-sm text-slate-500">
                Geplande gesprekken, gegroepeerd per dag. Na een gesprek kun je hier je vervolgkeuze doorgeven.
              </p>
            </div>
            <InterviewAgenda
              interviews={interviews}
              noteTextById={noteTextByInterviewId}
              onAutosaveNote={(id, t) => saveNote('interview', id, t)}
              onSendHeadsUp={(id, t) => {
                const inv = interviews.find((x) => x.id === id);
                if (!inv) return;
                sendHeadsUp('interview', id, t, {
                  candidateId: inv.candidateId,
                  vacancyId: inv.vacancyId,
                  recruiterId: inv.recruiterId,
                  candidateName: inv.candidateName,
                  vacancyTitle: inv.vacancyTitle,
                });
              }}
              onPortalFollowUp={(inv, kind, comment) => void handleInterviewPortalFollowUp(inv, kind, comment)}
            />
          </section>

          <section id="cec-plaatsingen" className="scroll-mt-6 pb-12">
            <div className="mb-4 flex items-center gap-2 border-b border-slate-200 pb-3">
              <Briefcase className="h-5 w-5 text-slate-400" aria-hidden />
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Plaatsingen</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Lopende en recente plaatsingen. Voeg commentaar toe voor je account- of recruitmentteam.
                </p>
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              {offers.length ? (
                offers.map((off) => (
                  <OfferCard
                    key={off.id}
                    offer={off}
                    noteText={notes[noteKey('offer', off.id)] || ''}
                    onAutosaveNote={(id, t) => saveNote('offer', id, t)}
                    onSendHeadsUp={(id, t) =>
                      sendHeadsUp('offer', id, t, {
                        candidateId: off.candidateId,
                        vacancyId: off.vacancyId,
                        recruiterId: off.recruiterId,
                        candidateName: off.candidateName,
                        vacancyTitle: off.vacancyTitle,
                      })
                    }
                    onAkkoord={(id) => {
                      void id;
                      pushActivity({
                        icon: 'sparkles',
                        actor: 'client',
                        actorLabel: user.name || 'Klant',
                        description: 'Akkoord gemeld (lokaal overzicht)',
                        candidateName: off.candidateName,
                        vacancyTitle: off.vacancyTitle,
                      });
                      dispatchGlobalToast('Akkoord genoteerd — informeer ook je recruiter.', 'info');
                    }}
                    onWijziging={(id) => {
                      void id;
                      dispatchGlobalToast('Gebruik het opmerkingenveld of neem contact op met recruitment.', 'info');
                    }}
                    onRisico={(id) => {
                      void id;
                      dispatchGlobalToast('Markeer risico’s bij voorkeur samen met recruitment.', 'info');
                    }}
                    onSavePlacementComment={handleSavePlacementComment}
                  />
                ))
              ) : (
                <EmptyState title="Geen plaatsingen" description="Zodra er een plaatsing is, verschijnt die hier." className="md:col-span-2" />
              )}
            </div>
          </section>

          {activity.length > 0 ? (
            <section className="border-t border-slate-200 pt-8">
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">Recent</h3>
              <ul className="space-y-2 text-sm text-slate-600">
                {activity.slice(0, 8).map((a) => (
                  <li key={a.id} className="flex flex-wrap gap-x-2 border-b border-slate-100 py-2 last:border-0">
                    <span className="text-slate-400">{new Date(a.createdAt).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' })}</span>
                    <span>{a.description}</span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      ) : null}

      <EngagementVacancyModal
        open={vacancyModal.open}
        loading={vacancyModal.loading}
        error={vacancyModal.error}
        vacancy={vacancyModal.data}
        onClose={closeVacancyModal}
      />
      <EngagementCandidateModal
        open={candidateModal.open}
        proposal={candidateModal.proposal}
        onClose={closeCandidateModal}
      />
    </div>
  );
};

export default ClientEngagementCenter;
